/**
 * Algolia instant search for the Offroad Living header.
 *
 * Replaces the theme's predictive search: the header field stays exactly as it looks, but
 * its keystrokes drive an Algolia InstantSearch instance whose results fill the whole page
 * between the header and the footer. Modelled on the live Neto search
 * (https://www.offroadliving.com.au/?query=bag), whose own implementation is archived at
 * `PD Sites/Neto/OffroadLiving/2025-07-pd-offroadliving/js/pd-search.js`.
 *
 * Built on the v5 search client - v4 reached end of life in August 2026 and the Algolia
 * Shopify app's dashboard now warns about it, so the old `algoliasearchLite` v3/v4 pattern
 * used on the Neto sites is deliberately not carried over.
 *
 * Configuration is injected by `snippets/algolia-search.liquid` as `window.PDAlgolia`.
 */

(() => {
  'use strict';

  const config = window.PDAlgolia;
  const root = document.getElementById('algolia-search');
  const inputs = Array.from(document.querySelectorAll('[data-algolia-search-input]'));

  if (!config || !root || inputs.length === 0) return;

  /* ── Lazy bootstrap ────────────────────────────────────────────────────── */
  /* The two Algolia libraries are ~350KB together and the header search sits on every page,
     so they are fetched on the shopper's first contact with the field rather than up front.
     A `?query=` already in the URL means results are wanted immediately, so that boots at
     once. */

  const LIBRARIES = [
    'https://cdn.jsdelivr.net/npm/algoliasearch@5.23.4/dist/lite/builds/browser.umd.js',
    'https://cdn.jsdelivr.net/npm/instantsearch.js@4.78.2/dist/instantsearch.production.min.js',
  ];

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = src;
      script.async = false;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });

  let booting = false;
  let bootFailed = false;

  const boot = async () => {
    if (booting) return;
    booting = true;

    try {
      /* Sequential, not Promise.all: InstantSearch has to see the search client already
         registered on window when it evaluates. */
      for (const src of LIBRARIES) {
        await loadScript(src);
      }

      await init();
    } catch (error) {
      console.error('[algolia-search] failed to load Algolia', error);
      /* Give the shopper a working search rather than a dead field: the submit handler below
         stops holding Enter, so the form falls through to Shopify's own search. */
      bootFailed = true;
      booting = false;
    }
  };

  inputs.forEach((input) => {
    input.addEventListener('focus', boot, { once: true });
    input.addEventListener('input', boot, { once: true });

    /* Enter pressed before the libraries arrive would submit the field's form into Shopify's
       native /search page. Hold it; the query already typed opens the Algolia results as soon
       as they load. */
    input.closest('form')?.addEventListener('submit', (event) => {
      if (bootFailed) return;
      event.preventDefault();
      boot();
    });
  });

  const hasQuery = Boolean(new URLSearchParams(window.location.search).get('query'));

  if (hasQuery) boot();

  /* ── Responsive chrome ─────────────────────────────────────────────────── */
  /* Wired outside init() so the search field opens on the first tap, without waiting for the
     libraries to arrive. */

  const desktop = window.matchMedia('(min-width: 990px)');

  /* The filter panel is a <details> so its black bar can collapse it on mobile and tablet.
     Desktop has no toggle, so it is forced open there and re-collapsed on the way back down. */
  const filters = document.getElementById('algolia-filters');

  const syncFilters = () => {
    if (filters) filters.open = desktop.matches;
  };

  syncFilters();
  desktop.addEventListener('change', syncFilters);

  const mobileInput = document.getElementById('algolia-mobile-input');

  const setMobileSearchOpen = (open) => {
    document.body.classList.toggle('algolia-mobile-search-open', open);

    document.querySelectorAll('[data-algolia-mobile-search-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
    });

    if (open) {
      mobileInput?.focus();
      return;
    }

    /* Closing the field clears the search, the same thing its own reset button does - leaving
       results up with no way to edit the query would be a dead end. */
    inputs.forEach((input) => {
      if (!input.value) return;
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  };

  document.querySelectorAll('[data-algolia-mobile-search-toggle]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      setMobileSearchOpen(!document.body.classList.contains('algolia-mobile-search-open'));
    });
  });

  /* Arriving on a ?query= link from a phone should show the field already filled in. */
  if (hasQuery && !desktop.matches) {
    document.body.classList.add('algolia-mobile-search-open');
  }

  /* ── Application ───────────────────────────────────────────────────────── */

  async function init() {
    /**
     * The v5 lite build registers itself as `window['algoliasearch/lite']` and exports
     * `liteClient` - it is not a bare callable the way the v4 `algoliasearchLite` global was.
     * The full v5 build and the old v4 global are both accepted as well so swapping the CDN
     * build does not silently break the page.
     */
    const resolveClientFactory = () => {
      const lite = window['algoliasearch/lite'];

      if (lite && typeof lite.liteClient === 'function') return lite.liteClient;
      if (typeof window.algoliasearch === 'function') return window.algoliasearch;
      if (window.algoliasearch && typeof window.algoliasearch.liteClient === 'function') {
        return window.algoliasearch.liteClient;
      }

      return null;
    };

    const createClient = resolveClientFactory();

    if (typeof instantsearch === 'undefined' || !createClient) {
      console.error('[algolia-search] Algolia libraries did not register their globals');
      return;
    }

    const INDEX = config.indexName;

    /* ── Index health ──────────────────────────────────────────────────────── */
    /* Three index-level settings hold this UI together - `attributeForDistinct` on the primary
       and on every sort replica, and `customRanking: asc(price)`. None is fully owned by the
       Algolia app: its own popularity replica arrived with `attributeForDistinct` null. If any
       of them is lost to a re-index, `distinct` silently stops collapsing variants and the grid
       repeats the same product once per variant - and on a replica that only shows up after the
       shopper changes the sort.

       Rather than trust the index, the storefront asks it. Each index it is about to use is
       tested for whether a distinct query actually returns unique products; if any cannot, the
       whole search drops to `filters: position=1`, which needs no index settings at all. The
       result is that duplicate cards cannot reach a shopper whatever state the index is in.
       `tools/algolia-ensure-index.js` still restores the better mode. */

    const algoliaQuery = async (index, body) => {
      const response = await fetch(
        `https://${config.appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(index)}/query`,
        {
          method: 'POST',
          headers: {
            'X-Algolia-API-Key': config.searchKey,
            'X-Algolia-Application-Id': config.appId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) return null;

      return response.json();
    };

    /** Does a distinct query against this index come back one row per product? */
    const dedupesCleanly = async (index) => {
      const result = await algoliaQuery(index, {
        query: '',
        hitsPerPage: 20,
        distinct: true,
        attributesToRetrieve: ['id'],
      });

      if (!result || !Array.isArray(result.hits)) return false;
      /* Nothing to collapse tells us nothing - take it as healthy. */
      if (result.hits.length < 2) return true;

      return new Set(result.hits.map((hit) => hit.id)).size === result.hits.length;
    };

    const CATEGORY_ATTRIBUTE = config.categoryAttribute || 'collections';
    const BRAND_ATTRIBUTE = config.brandAttribute || 'vendor';
    const PRICE_ATTRIBUTE = config.priceAttribute || 'price';

    /* B2B customers buy on their company's terms, which the index does not hold, so search
       must not quote them a retail price. Set from `customer.b2b?` in the snippet, which
       also drops the price filter's container - the two have to stay in step. */
    const HIDE_PRICES = config.hidePrices === true;

    /* ── Helpers ───────────────────────────────────────────────────────────── */

    /** Cards are assembled as HTML strings, so every interpolated value is escaped. */
    const escapeHtml = (value) =>
      String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    /**
     * Formats a number using the shop's own money format, so the search cards print prices
     * the same way every other price on the site does.
     */
    const formatMoney = (amount) => {
      const value = Number(amount);

      if (!isFinite(value)) return '';

      const withDecimals = value.toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const withoutDecimals = Math.round(value).toLocaleString('en-AU');
      const format = config.moneyFormat || '${{amount}}';

      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withoutDecimals)
        .replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/g, withDecimals);
    };

    /**
     * The price slider's own labels. Live prints these as whole dollars - "$49" and "$6,369" -
     * where the shop's money format carries two decimals, so the slider cannot reuse
     * `formatMoney`. Product cards keep the decimals; only the slider ends drop them.
     */
    const formatMoneyWhole = (amount) => {
      const value = Number(amount);

      if (!isFinite(value)) return '';

      const rounded = Math.round(value).toLocaleString('en-AU');
      const format = config.moneyFormat || '${{amount}}';

      return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, rounded);
    };

    /** Asks the Shopify CDN for a right-sized image instead of the full-resolution original. */
    const resizeImage = (url, width) => {
      if (!url) return '';

      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(width));
        return parsed.href;
      } catch (error) {
        return url;
      }
    };

    /**
     * Normalises one Algolia record into the fields the card template needs.
     *
     * Read off a real record rather than the app's documentation, which differs:
     *   objectID   is the bare variant id (43696391946307), not "<product>_<variant>"
     *   id         is the product id
     *   image      is the variant image, product_image the product's featured one
     *   there is no `collections` attribute - categories live in `tags`
     *
     * Records are one per variant, so a multi-variant product's own record is whichever
     * variant ranked first. Its `price` is therefore not necessarily the cheapest, which
     * would print a card price the product page contradicts. Multi-variant cards use
     * `variants_min_price` behind a "From" instead - the same thing the live Neto site does
     * with its `has_child` flag.
     */
    const mapHit = (hit) => {
      const handle = hit.handle || '';
      const variantsCount = Number(hit.variants_count || 0);
      const hasOptions = variantsCount > 1;

      const price = hasOptions
        ? Number(hit.variants_min_price)
        : Number(hit.price);

      /* Compare-at is only meaningful against a single variant's own price; across a range
         the record's min price and max compare-at belong to different variants. */
      const compareAt = hasOptions ? 0 : Number(hit.compare_at_price || 0);

      const available = hasOptions
        ? Number(hit.variants_inventory_count || 0) > 0
        : Boolean(hit.inventory_available);

      return {
        handle,
        url: handle ? `/products/${handle}` : '#',
        title: hit.title || '',
        sku: hit.sku || '',
        image: hit.image || hit.product_image || '',
        price,
        compareAt: compareAt > price ? compareAt : 0,
        pricePrefix: hasOptions ? 'From ' : '',
        variantId: hit.objectID,
        variantsCount,
        available,
        hasOptions,
      };
    };

    /**
     * Keeps the category filter to things a shopper would recognise as a category.
     *
     * Products carry each of their collections as a tag twice - once titled ("Heat Shrink")
     * and once as its handle ("heat-shrink-1") - so the handle form is dropped as a
     * duplicate: no spaces, and nothing but lowercase, digits and hyphens. Warehouse tags
     * ("Bulky/Non DG") are categories to nobody, so those come off a denylist that is a
     * theme setting rather than a constant here.
     */
    const excluded = new Set(
      (config.categoryExclusions || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
    );

    const isHandleForm = (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

    const filterCategories = (items) =>
      items.filter((item) => !isHandleForm(item.label) && !excluded.has(item.label.toLowerCase()));

    /* ── Card template ─────────────────────────────────────────────────────── */

    /**
     * The card is built as an HTML string and handed to the renderer whole, rather than
     * described with the `html` template.
     *
     * That is because of one attribute. Horizon's <product-card> is a real custom element
     * that throws MissingRefError unless its link carries ref="productCardLink", and `ref` is
     * a reserved prop in the Preact renderer InstantSearch uses - passing it through the
     * template made Preact try to write `.current` onto the string and every card failed to
     * render. Raw HTML has no reserved attribute names, and the element upgrades cleanly.
     *
     * The wrapper is display:contents, so it adds nothing to the layout.
     *
     * Class names throughout are the theme's own, so the card spec already signed off in
     * `custom.css` (grey ground, 20px/0 radius, "Now" against a struck "RRP", black CTA,
     * folded sale ribbon) applies to search results without being restated.
     */
    const buildCard = (item, hit) => {
      const title = instantsearch.highlight({ attribute: 'title', hit });

      const price = item.compareAt
        ? `<span class="price price-item--sale">${formatMoney(item.price)}</span>
           <span class="compare-at-price price-item--regular">${formatMoney(item.compareAt)}</span>`
        : `<span class="price">${escapeHtml(item.pricePrefix)}${formatMoney(item.price)}</span>`;

      const icon = `<span class="svg-wrapper add-to-cart-icon" aria-hidden="true">${config.icons.addToCart}</span>`;

      let cta;

      if (!item.available) {
        cta = `<div class="product-form-buttons">
            <a class="button add-to-cart-button" href="${escapeHtml(item.url)}">
              <span class="add-to-cart-text">
                <span class="add-to-cart-text__content">${escapeHtml(config.strings.soldOut)}</span>
              </span>
            </a>
          </div>`;
      } else if (item.hasOptions || !item.variantId) {
        /* Multi-variant products show the bulk-order grid on the product page rather than a
           variant picker, so their cards send the shopper through instead of adding blind. */
        cta = `<div class="product-form-buttons product-card__see-options">
            <a class="button add-to-cart-button" href="${escapeHtml(item.url)}">
              <span class="add-to-cart-text">
                ${icon}
                <span class="add-to-cart-text__content">${escapeHtml(config.strings.seeOptions)}</span>
              </span>
            </a>
          </div>`;
      } else {
        cta = `<div class="product-form-buttons">
            <button
              type="button"
              class="button add-to-cart-button"
              data-algolia-add-to-cart
              data-variant-id="${escapeHtml(item.variantId)}"
              data-product-url="${escapeHtml(item.url)}"
            >
              <span class="add-to-cart-text">
                ${icon}
                <span class="add-to-cart-text__content">${escapeHtml(config.strings.addToCart)}</span>
              </span>
            </button>
          </div>`;
      }

      let badge = '';

      if (!item.available) {
        badge = `<div class="product-badges product-badges--top-left">
            <div class="product-badges__badge product-badges__badge--rectangle color-scheme-3">${escapeHtml(
              config.strings.soldOut
            )}</div>
          </div>`;
      } else if (item.compareAt) {
        badge = `<div class="product-badges product-badges--top-left">
            <div class="product-badges__badge product-badges__badge--rectangle color-scheme-1">${escapeHtml(
              config.strings.sale
            )}</div>
          </div>`;
      }

      return `<product-card class="product-card size-style" data-sku="${escapeHtml(item.sku)}">
          <a
            class="product-card__link algolia-card__link"
            href="${escapeHtml(item.url)}"
            ref="productCardLink"
            aria-label="${escapeHtml(item.title)}"
          ></a>

          <div class="product-card__content product-grid__card">
            <span class="card-gallery algolia-card__media">
              <img
                src="${escapeHtml(resizeImage(item.image, 500))}"
                alt="${escapeHtml(item.title)}"
                width="500"
                height="500"
                loading="lazy"
              >
            </span>

            <div class="algolia-card__rating pd_thumb_ruk_rating_snippet" data-sku="${escapeHtml(item.sku)}"></div>

            <p class="algolia-card__title" role="heading" aria-level="3">${title}</p>

            ${HIDE_PRICES ? '' : `<div class="algolia-card__price">${price}</div>`}

            ${cta}
          </div>

          ${badge}
        </product-card>`;
    };

    const renderHit = (hit, { html }) =>
      html`<div
        class="algolia-card-shell"
        dangerouslySetInnerHTML=${{ __html: buildCard(mapHit(hit), hit) }}
      ></div>`;

    /* ── Add to cart ───────────────────────────────────────────────────────── */

    /**
     * Horizon's cart icon and drawer listen for a `cart:update` on the document and read
     * `event.detail.data.itemCount` off it, so the event has to carry that shape - a bare
     * CustomEvent leaves `detail` null and the listener throws on it.
     *
     * `source` deliberately is not 'product-form-component': the cart icon treats that one
     * as a delta to add to its running count, and what is passed here is the absolute count
     * read back from the cart.
     */
    async function addToCart(button) {
      const variantId = button.dataset.variantId;
      const productUrl = button.dataset.productUrl;

      button.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`${config.routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });

        if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

        const cart = await fetch(`${config.routes.cart}.js`).then((result) => result.json());

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'algolia-search',
              data: {
                source: 'algolia-search',
                itemCount: cart.item_count,
                variantId: String(variantId),
              },
            },
          })
        );
      } catch (error) {
        console.error('[algolia-search] add to cart failed', error);
        window.location.href = productUrl;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    /* Delegated, because the cards are raw HTML with no handlers bound to them. One listener
       outlives every re-render. */
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-algolia-add-to-cart]');

      if (!button) return;

      event.preventDefault();
      addToCart(button);
    });

    /* ── Open / close ──────────────────────────────────────────────────────── */

    const setOpen = (open) => {
      document.body.classList.toggle('algolia-open', open);
      root.toggleAttribute('hidden', !open);
    };

    /* Clearing the query re-renders the search box connector, which closes the panel. */
    const closeSearch = () => {
      inputs.forEach((input) => {
        input.value = '';
      });
      search.helper.setQuery('').search();
    };

    /* ── Search instance ───────────────────────────────────────────────────── */

    const searchClient = createClient(config.appId, config.searchKey);

    /* `singleIndex` keeps the query on a bare `?query=` rather than the index-scoped
       `?<index>[query]=` that v4 routing defaults to - the live site's URL shape. Sorting by
       the primary index is the default, so it is dropped rather than written out as
       `&sortBy=shopify_products` on every search. */
    const baseStateMapping = instantsearch.stateMappings.singleIndex(INDEX);

    const stateMapping = {
      stateToRoute(uiState) {
        const route = baseStateMapping.stateToRoute(uiState);

        if (route.sortBy === INDEX) delete route.sortBy;

        return route;
      },
      routeToState(routeState) {
        return baseStateMapping.routeToState(routeState);
      },
    };

    const search = instantsearch({
      indexName: INDEX,
      searchClient,
      routing: {
        router: instantsearch.routers.history({ cleanUrlOnDispose: false }),
        stateMapping,
      },
      future: { preserveSharedStateOnUnmount: true },
    });

    /* The header field is the theme's own markup, so rather than let the widget draw a search
       box we bind the existing input to the search-box connector.

       Opening and closing the panel hangs off this render rather than off the instance's
       `onStateChange`: that hook has to hand routing back its own work by calling
       `setUiState`, and while the URL did update from it the panel never did. This connector
       renders on every query change, which is exactly the signal wanted, and it leaves
       routing on its default path. */
    const connectHeaderInput = instantsearch.connectors.connectSearchBox(
      ({ refine, query }, isFirstRender) => {
        if (isFirstRender) {
          /* Anything typed while the libraries were still downloading is already sitting in
             the field, and produced no event this connector saw. */
          const typedAhead = inputs.map((input) => input.value).find((value) => value.trim());

          /* Deferred until start() has returned: a refine made during this first render never
             reaches the stored UI state, so the URL kept no ?query= for it. */
          if (typedAhead && typedAhead !== query) setTimeout(() => refine(typedAhead));

          /* A query carried in on the URL - a shared link, or a refresh while search was
             open - has to fill the field and open the panel straight away. */
          if (query) {
            inputs.forEach((input) => {
              input.value = query;
            });
          }

          setOpen(Boolean((typedAhead || query || '').trim()));

          inputs.forEach((input) => {
            input.addEventListener('input', (event) => refine(event.target.value));
            input.addEventListener('keydown', (event) => {
              if (event.key === 'Escape') closeSearch();
              /* The field is inside a <form>; Enter would reload into Shopify's own search. */
              if (event.key === 'Enter') event.preventDefault();
            });
          });

          document.querySelectorAll('[data-algolia-search-reset]').forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              closeSearch();
            });
          });

          return;
        }

        setOpen(Boolean(query.trim()));

        inputs.forEach((input) => {
          if (document.activeElement !== input && input.value !== query) {
            input.value = query;
          }
        });
      }
    );

    /* Labels match the live Neto select exactly. A replica only exists once that sort order is
       switched on in the Algolia app, and pointing `sortBy` at a missing one makes every search
       through it fail - so each is probed, and only the ones that answer are offered. */
    const SORT_CANDIDATES = [
      { value: `${INDEX}_recently_ordered_count_desc`, label: 'Sort By Most Popular' },
      { value: `${INDEX}_price_asc`, label: 'Sort By Lowest Price' },
      { value: `${INDEX}_price_desc`, label: 'Sort By Highest Price' },
    ];

    const liveSorts = [];

    for (const candidate of SORT_CANDIDATES) {
      if (await algoliaQuery(candidate.value, { query: '', hitsPerPage: 0 })) liveSorts.push(candidate);
    }

    /* `auto` asks the indices; the explicit modes are escape hatches. Every index the shopper
       can reach has to dedupe, or none of them is trusted - the sort switches index underneath
       a single `configure`, so the mode has to hold for all of them at once. */
    let dedupeMode = config.dedupeMode || 'auto';

    if (dedupeMode === 'auto') {
      const reachable = [INDEX, ...liveSorts.map((s) => s.value)];
      const results = await Promise.all(reachable.map(dedupesCleanly));
      const unhealthy = reachable.filter((_, i) => !results[i]);

      dedupeMode = unhealthy.length ? 'position' : 'distinct';

      if (unhealthy.length) {
        console.warn(
          `[algolia-search] distinct is not collapsing variants on ${unhealthy.join(', ')} - ` +
            'falling back to first-variant-only. Run tools/algolia-ensure-index.js --fix to ' +
            'restore attributeForDistinct.'
        );
      }
    }

    search.addWidgets([
      connectHeaderInput({}),

      /* The index holds one record per variant, so a six-length cable is six hits and the
         grid repeats the same card six times. Two ways to collapse that:

         'distinct'  - the right one, and what runs today. `attributeForDistinct: id` is set
                       on shopify_products and on all three sort replicas, so Algolia keeps
                       the best-matching variant of each product and reports honest counts.
                       It has to be set on the replicas too: without it, changing the sort
                       quietly brought the duplicates back.
         'position'  - fallback for if that index setting is ever lost. Keeps only each
                       product's first variant, so a term appearing solely on a later variant
                       (its SKU, its option value) stops finding the product at all.

         The filter goes through `filters`, not `numericFilters`: the price slider drives the
         helper's managed numeric refinements, and mixing the two raises "Can't switch from
         the advanced to the managed API" before a single search runs. */
      /* `facetingAfterDistinct` counts the facets over the collapsed products rather than the
         raw variant records. Without it a filter read "Cables 2" beside "1 Product Found" -
         two variants of one product.

         It only tells the truth because the index keeps the *cheapest* variant of each product
         (customRanking `asc(price)` breaks the tie between variants), so the price a facet sees
         is the product's minimum - the same figure the card prints as "From $X". Were the
         representative variant arbitrary, the price slider would start excluding products whose
         advertised "From" price is inside the chosen range. */
      instantsearch.widgets.configure({
        hitsPerPage: config.hitsPerPage || 36,
        ...(dedupeMode === 'position'
          ? { filters: 'position=1' }
          : { distinct: true, facetingAfterDistinct: true }),
      }),

      instantsearch.widgets.stats({
        container: '#algolia-stats',
        templates: {
          text: (data, { html }) => {
            if (data.nbHits === 0) return html`<span>No results</span>`;
            if (data.nbHits === 1) return html`<span>1 Product Found</span>`;
            return html`<span>${data.nbHits.toLocaleString('en-AU')} Products Found</span>`;
          },
        },
      }),

      instantsearch.widgets.refinementList({
        container: '#algolia-categories',
        attribute: CATEGORY_ATTRIBUTE,
        operator: 'or',
        limit: 99,
        sortBy: ['name:asc'],
        transformItems: filterCategories,
        cssClasses: {
          list: 'algolia-facet__list',
          item: 'algolia-facet__item',
          selectedItem: 'algolia-facet__item--selected',
          label: 'algolia-facet__link',
          checkbox: 'algolia-facet__checkbox',
          count: 'algolia-facet__count',
        },
      }),

      instantsearch.widgets.refinementList({
        container: '#algolia-brands',
        attribute: BRAND_ATTRIBUTE,
        operator: 'or',
        limit: 99,
        sortBy: ['count:desc', 'name:asc'],
        cssClasses: {
          list: 'algolia-facet__list',
          item: 'algolia-facet__item',
          selectedItem: 'algolia-facet__item--selected',
          label: 'algolia-facet__link',
          checkbox: 'algolia-facet__checkbox',
          count: 'algolia-facet__count',
        },
      }),

      /* Left out for a B2B customer, whose sidebar has no `#algolia-price` to mount into. */
      ...(HIDE_PRICES
        ? []
        : [
            instantsearch.widgets.rangeSlider({
              container: '#algolia-price',
              attribute: PRICE_ATTRIBUTE,
              tooltips: {
                format: (value) => formatMoneyWhole(value),
              },
            }),
          ]),

      instantsearch.widgets.clearRefinements({
        container: '#algolia-clear-refinements',
        templates: { resetLabel: 'Clear Filters' },
      }),

      instantsearch.widgets.hits({
        container: '#algolia-hits',
        templates: {
          item: renderHit,
          empty: (data, { html }) => html`
            <div class="algolia-empty">
              No results found matching <strong>${data.query}</strong>.
            </div>
          `,
        },
        cssClasses: {
          list: 'algolia-hits__list',
          item: 'algolia-hits__item',
        },
      }),

      instantsearch.widgets.pagination({
        container: '#algolia-pagination',
        showFirst: false,
        showLast: false,
        padding: 2,
      }),

      /* Registered with everything else, before start(). Adding a widget to a started instance
         rebuilds the search state from the stored UI state, which does not yet hold a query
         refined during the header input's first render - so whatever the shopper typed while
         the libraries were loading was wiped back to an empty search. */
      instantsearch.widgets.sortBy({
        container: '#algolia-sort',
        items: [{ value: INDEX, label: 'Sort By' }, ...liveSorts],
      }),
    ]);

    search.start();

    /* ── Filter toggle ─────────────────────────────────────────────────────── */

    const toggle = document.getElementById('algolia-toggle-filters');

    if (toggle) {
      toggle.addEventListener('click', () => {
        const hidden = root.classList.toggle('algolia-results--no-filters');

        toggle.textContent = hidden ? config.strings.showFilters : config.strings.hideFilters;
        toggle.setAttribute('aria-expanded', String(!hidden));
      });
    }

    /* ── REVIEWS.io stars ──────────────────────────────────────────────────── */
    /* The store already runs REVIEWS.io as an app embed; the same snippet the live site calls
       is re-run after each render so newly drawn cards pick up their ratings. */

    search.on('render', () => {
      if (typeof window.ratingSnippet !== 'function') return;

      window.ratingSnippet('pd_thumb_ruk_rating_snippet', {
        store: 'offroadliving-com-au',
        mode: 'default',
        color: '#0E1311',
        linebreak: false,
        lang: 'en',
        usePolaris: true,
        showEmptyStars: false,
      });
    });

  }
})();
